import type { Key, ReactElement, ReactNode } from "react";
import { Children, cloneElement } from "react";

import type { KeysOfUnion, SmooshedObjectRecord } from "../types/react";

export const DISPLAY_NAME_FOR_UNKNOWN = "Unknown";

/**
 * Получить displayName функционального компонента
 * @param child
 */
export const getDisplayName = (child: ReactNode): string => {
    return (
        ((child as ReactElement)?.type as any)?.displayName ||
        DISPLAY_NAME_FOR_UNKNOWN
    );
};

/**
 * Получить displayName функционального компонента без prefix
 * @param child
 * @param prefix
 */
export const getDisplayNameWithoutPrefix = (
    child: ReactNode,
    prefix: string,
): string => {
    return getDisplayName(child).replace(`${prefix}.`, "");
};

export type TGetChildrenRecordComponentsSettings = Record<
    string,
    {
        /** Свойства компонента */
        props?: Record<string, any>;
        /** Если компонент является обязательным, приложение будет требовать его существование */
        required?: boolean;
        /** Используем, когда требуется наличие хотя бы одного компонента из набора компонентов с таким же флагом */
        optionalRequired?: boolean;
        /** Указываем, если нам не нужно добавлять элемент в результирующий объект */
        skipElement?: boolean;
    }
>;

export type TGetChildrenRecordOptions = {
    /** Возвращаем результат getChildrenRecord в виде массива ReactElement */
    isArrayResult?: boolean;
};

const getElementsName = (elementsName: string[], prefix = "") =>
    elementsName
        .map((name) => {
            return prefix ? `${prefix}.${name}` : name;
        })
        .join(", ");

/**
 * Функция клонирует переданные children и мутирует их на основе componentsSettings.
 *
 * В зависимости от параметра options.isArrayResult возвращает:
 * Если options.isArrayResult = false, то объект, где ключ - displayName этого компонента (без префикса!), а значение - ReactElement;
 * Если options.isArrayResult = true, то массив ReactElement.
 *
 * @param children - ReactNode,
 * @param prefix - указываем название рут-компонента, которому будут принадлежать дети,
 * @param componentsSettings - объект с настройками для каждого ребёнка.
 * @param options - объект для настройки самой функции
 */
export function getChildrenRecord(
    children: ReactNode,
    prefix: string,
    componentsSettings: TGetChildrenRecordComponentsSettings,
    options?: TGetChildrenRecordOptions & { isArrayResult?: false },
): Record<string, ReactElement>;

export function getChildrenRecord(
    children: ReactNode,
    prefix: string,
    componentsSettings: TGetChildrenRecordComponentsSettings,
    options?: TGetChildrenRecordOptions & { isArrayResult?: true },
): ReactElement[];

export function getChildrenRecord(
    children: ReactNode,
    prefix: string,
    componentsSettings: TGetChildrenRecordComponentsSettings,
    options: TGetChildrenRecordOptions = { isArrayResult: false },
): Record<string, ReactElement> | ReactElement[] {
    let unknownElementCounter = 0;
    const result: Record<string, ReactElement> = {};
    const arrayResult: ReactElement[] = [];
    const allRequired: string[] = [];
    const allOptionalRequired: string[] = [];

    Object.entries(componentsSettings).forEach(([key, item]) => {
        if (item.required) {
            allRequired.push(key);
        }

        if (item.optionalRequired) {
            allOptionalRequired.push(key);
        }
    });

    Children.forEach(Children.toArray(children), (_child) => {
        const child = _child as ReactElement;
        const displayName = getDisplayNameWithoutPrefix(child, prefix);
        const currentSettings = componentsSettings[displayName];

        /**
         * Если ребёнок не имеет особой настройки, то просто возвращаем его
         */
        let currentElement = child;

        if (currentSettings) {
            if (currentSettings.skipElement) {
                return;
            }

            const currentProps: Record<string, any> = {
                ...currentSettings.props,
                ...child.props,
                key: `${(child as { key: Key }).key}-${displayName}`,
            };

            currentElement = cloneElement(child, currentProps);
        } else if (displayName === DISPLAY_NAME_FOR_UNKNOWN) {
            // По-умолчанию, скипаем всех неизвестных детей
            return;
        }

        if (options.isArrayResult) {
            arrayResult.push(currentElement);
        } else {
            let currentDisplayName = displayName;

            if (displayName === DISPLAY_NAME_FOR_UNKNOWN) {
                // Все неизвестные элементы попадут в массив, но будут иметь имя вида Unknown-N
                currentDisplayName = `${displayName}-${unknownElementCounter}`;
                unknownElementCounter++;
            }

            result[currentDisplayName] = currentElement;
        }
    });

    checkAndThrowErrors({
        allOptionalRequired,
        allRequired,
        result,
        arrayDisplayNamesResult: arrayResult.map((item) =>
            getDisplayNameWithoutPrefix(item, prefix),
        ),
        prefix,
    });

    return options?.isArrayResult ? arrayResult : result;
}

type TCheckAndThrowErrors = {
    allOptionalRequired: string[];
    allRequired: string[];
    result: Record<string, ReactElement>;
    arrayDisplayNamesResult: string[];
    prefix: string;
};

function checkAndThrowErrors({
    allOptionalRequired,
    allRequired,
    result,
    arrayDisplayNamesResult,
    prefix,
}: TCheckAndThrowErrors) {
    const messageErrorList: string[] = [];
    let remainingRequired: string[];
    let isOptionalRequiredExist: boolean;

    if (arrayDisplayNamesResult.length) {
        remainingRequired = allRequired.filter(
            (item) => !arrayDisplayNamesResult.includes(item),
        );

        isOptionalRequiredExist =
            !allOptionalRequired.length ||
            Boolean(
                allOptionalRequired.find((item) =>
                    arrayDisplayNamesResult.includes(item),
                ),
            );
    } else {
        remainingRequired = allRequired.filter((item) => !result[item]);

        isOptionalRequiredExist =
            !allOptionalRequired.length ||
            Boolean(allOptionalRequired.find((item) => result[item]));
    }

    if (remainingRequired.length) {
        const currentElementsName = getElementsName(remainingRequired);
        messageErrorList.push(
            `${prefix || "Компонент"} должен содержать все эти компоненты: ${currentElementsName}!`,
        );
    }

    if (!isOptionalRequiredExist) {
        const currentElementsName = getElementsName(allOptionalRequired);
        messageErrorList.push(
            `${prefix || "Компонент"} должен включать один из этих компонентов: ${currentElementsName}!`,
        );
    }

    if (messageErrorList.length) {
        throw new Error(messageErrorList.join("\n"));
    }
}

/**
 * Разделяем пропсы на переданные ключи в extractedPropsKeys и оставшиеся из props. Это нам позволяет вытянуть любой
 * ключ из всего типа (в том числе и из всех объединений) с корректным типом (если ключ не является общим,
 * то к его типу добавится undefined). Тем самым нам не нужно каждый раз сужать пропсы, чтобы достать ключ из конкретного
 * объединения.
 * @example
 * type TProps = {
 *     color: string;
 *     view: string;
 * } & ({
 *     type: 'type1';
 *     field1: number
 * } | {
 *     type: 'type2';
 *     key1: string[];
 * });
 *
 * const testProps = {} as TTestProps;
 * const {
 *  field1, // number | undefined
 *  currentProps: { color, view, type } // Omit<SmooshedObjectRecord<TTestProps>, 'field1'>
 * } = extractProps(testProps, ['field1']);
 *
 * @param _props - объект с динамическими свойствами
 * @param extractedPropsKeys - ключи, которые хотим вытянуть из объекта props
 * @deprecated необходимо правильно создавать юнионы, где каждая часть содержит все поля других частей.
 * Если в части объединения поля не должно существовать, ему необходимо указать тип optional never (field?: never)
 */
export const extractProps = <T, Keys extends KeysOfUnion<T>>(
    _props: T,
    extractedPropsKeys: Keys[],
) => {
    const props = _props as SmooshedObjectRecord<T>;
    const copyData = { ...props };
    const extractedProps = extractedPropsKeys.reduce(
        (acc, key) => {
            acc[key] = props[key];
            delete copyData[key];
            return acc;
        },
        {} as {
            [Key in Keys]: (typeof props)[Key];
        },
    );

    return {
        currentProps: copyData as Omit<typeof props, Keys>,
        ...extractedProps,
    };
};
